import { defineStore } from "pinia";
import { ref, computed } from "vue";

import {
    Partition,
    GlassPartition,
    GlassThinknessPartition,
    OutOfSquare,
} from "@/app/domain";
import {
    GlassSortClearCode,
    GlassThicknessCode3_8,
    PartitionGlassCode,
} from "@/app/constants";

import { useSpacesStore } from "./spaces.store";
import { usePartitionGlassArea, usePartitionPrice } from "@/app/composables";
import { PricingService } from "@/app/services/pricing.service";
import { useNewEstimateStore } from "./estimateStore";
import { defaultGlassStructureCode } from "@/app/constants/glass";
import { useDictStore } from "@/stores/dictStore";
import { getKompasMultiplier } from "@/app/predicates/systems.predicates";
import { DimensionValue } from "@/app/domain/entities/dimensions";

export const usePartitionStore = defineStore(
    "partition-store",
    () => {
        const pricingService = new PricingService();

        const dictStore = useDictStore();
        const spacesStore = useSpacesStore();
        const estimateStore = useNewEstimateStore();

        const installationSystem = computed(
            () => estimateStore.estimate.installedSystemCode,
        );

        const activeSpace = computed(() => spacesStore.activeSpace);
        const activePartitions = ref<Partition | null>(null);

        const activeGlassPartition = ref<GlassPartition | null>();
        const activeGlassThinknessPartition =
            ref<GlassThinknessPartition | null>();
        const activeOutOfSquare = ref<OutOfSquare | null>();

        const hasActivePartition = computed(
            () => activePartitions.value !== null && activePartitions.value !== undefined
        );

        usePartitionGlassArea();
        usePartitionPrice();

        const $reset = () => {
            activeGlassPartition.value = null;
            activeGlassThinknessPartition.value = null;
            activePartitions.value = null;
            activeOutOfSquare.value = null;
        };

        const $restorePartitions = () => {
            const partition = activeSpace.value?.getActivePartition();

            if (!partition) return;

            setActivePartition(partition);
        };

        const setActivePartition = (partition?: Partition) => {
            if (!partition) return;

            const glass = partition?.getActiveGlass();
            const glassThinkness = partition?.getActiveGlassThinkness();

            const outOfSquare = partition?.getActiveOutOfSquare();

            activePartitions.value = partition;
            activeGlassPartition.value = glass;
            activeGlassThinknessPartition.value = glassThinkness;
            activeOutOfSquare.value = outOfSquare;
        };

        const addPartition = () => {
            if (!activeSpace.value) {
                return;
            }

            const partition = new Partition({
                qty: 1,
                structureType: PartitionGlassCode,
                partitionPrice: 0,
            });

            activeSpace.value.addComponent(partition);
            activePartitions.value = partition;

            addGlassPartition();
            addGlassThinknessPartition();
            addOutOfSquare();
        };

        const getDefaultGlassThickness = () => {
            const defaultThicknessesInfo = dictStore.findGlassDefaultThick(
                installationSystem.value,
            );
            const thickValue = defaultThicknessesInfo?.partition_thick

            return thickValue ||
                defaultGlassStructureCode[installationSystem.value] ||
                GlassThicknessCode3_8;
        };

        const getDefaultGlassSort = () => {
            const glassSorts = dictStore.findGlassSort(installationSystem.value);
            const defaultGlassThickCode = getDefaultGlassThickness();

            const defaultGlassType = glassSorts.find((el) => {
                const glassType = dictStore.findGlassSortThicknesses(el.code);
                const glassThickness = glassType?.thicknesses?.find(
                    (thick) => thick === defaultGlassThickCode,
                );

                if (glassThickness) {
                    return el;
                }
            });

            return defaultGlassType?.code
        };

        const addGlassPartition = () => {
            const multiplier = getKompasMultiplier(installationSystem.value as string);
            const defaultGlassSortCode = getDefaultGlassSort();
            const glassPartition = new GlassPartition({
                structureType: defaultGlassSortCode || GlassSortClearCode,
            });

            glassPartition.dimensions.area = new DimensionValue(glassPartition.dimensions.area.getValue() * multiplier);
            activePartitions.value?.addComponent(glassPartition);
            activeGlassPartition.value = glassPartition;
        };

        const addGlassThinknessPartition = () => {
            const defaultGlassThickCode = getDefaultGlassThickness();
            const glassThinksnessPartition = new GlassThinknessPartition({
                structureType: defaultGlassThickCode,
            });

            activeGlassPartition.value?.addComponent(glassThinksnessPartition);
            activeGlassThinknessPartition.value = glassThinksnessPartition;
        };

        const addOutOfSquare = () => {
            const outOfSquare = new OutOfSquare({});

            activePartitions.value?.addComponent(outOfSquare);
            activeOutOfSquare.value = outOfSquare;
        };

        const recalcPartitionSum = () => {
            const sum = pricingService.calculatePartitionPrice();
            activePartitions.value?.update({ sum });
        };

        return {
            activeSpace,
            activePartitions,
            activeGlassPartition,
            activeGlassThinknessPartition,
            activeOutOfSquare,
            hasActivePartition,
            setActivePartition,
            addPartition,
            addGlassPartition,
            addGlassThinknessPartition,
            addOutOfSquare,
            recalcPartitionSum,
            $restorePartitions,
            $reset,
        };
    },
    { persist: true },
);
